import { addressHtml, escapeHtml as esc, printHtml } from '../../utils/invoice'
import { brandColors } from '../../data/brandColors'
import type { InboxOrder } from '../../core/types/adminRequests'
import type { Product } from '../../core/types/catalog'
import { groupByBundle, requestBundleOf } from '../../core/rules/orderBundles'

type Salon = { name: string; address: string; email: string }

/**
 * Pakbon voor in de doos: adres, wat erin hoort (om af te vinken) en een plek voor
 * Sarah's handgeschreven kaartje. Geen prijzen: het kan een cadeau zijn.
 */
export function packingSlipHtml(
  o: InboxOrder,
  productById: (id: string) => Product | undefined,
  salon: Salon,
  gift: string | null,
  /** Naam van een protocol, voor het kopje boven een bundel ("in als set"). */
  routineName: (id: string) => string | undefined = () => undefined,
) {
  const a = o.order.address
  const row = (l: InboxOrder['order']['lines'][number]) => {
    const p = productById(l.productId)
    const name = p ? `${p.brand} ${p.name}` : l.productId
    return `<tr><td class="box"></td><td class="qty">${l.qty} ×</td><td>${esc(name)}${p?.volume ? ` <span class="muted">· ${esc(p.volume)}</span>` : ''}${
      l.subscribe ? ' <span class="muted">· abonnement</span>' : ''
    }</td></tr>`
  }
  const rows = groupByBundle(o.order.lines, requestBundleOf)
    .map((g) =>
      g.routineId
        ? `<tr><td colspan="3" class="set">Als set: ${esc(routineName(g.routineId) ?? 'protocol')}</td></tr>${g.lines.map(row).join('')}`
        : g.lines.map(row).join(''),
    )
    .join('')

  return `<!doctype html><html lang="nl"><head><meta charset="utf-8"><title>Pakbon ${esc(o.id)}</title>
<style>
@page{margin:18mm}
body{font-family:Inter,system-ui,sans-serif;color:${brandColors.ink};font-size:13px;line-height:1.5}
h1{font-family:Fraunces,Georgia,serif;font-weight:300;font-size:26px;margin:0}
.muted{color:${brandColors.muted}}.head{display:flex;justify-content:space-between;gap:24px}
.to{margin-top:28px;font-size:15px}
table{width:100%;border-collapse:collapse;margin-top:28px}
td{padding:10px 6px;border-bottom:1px solid ${brandColors.line};vertical-align:top}
.box{width:16px}.box::before{content:"";display:inline-block;width:12px;height:12px;border:1px solid ${brandColors.ink};border-radius:2px}
.qty{width:44px;white-space:nowrap}
.set{padding-top:16px;font-size:12px;color:${brandColors.muted};border-bottom:none}
.note{margin-top:24px;padding:12px 14px;border:1px solid ${brandColors.line};border-radius:6px}
.card{margin-top:40px;height:110px;border:1px dashed ${brandColors.line};border-radius:6px;padding:12px}
</style></head><body>
<div class="head"><div><h1>${esc(salon.name)}</h1><p class="muted">Pakbon · ${esc(o.id)}</p></div>
<div style="text-align:right" class="muted">${esc(salon.address)}<br>${esc(salon.email)}</div></div>
<p class="to"><strong>${esc(`${a.firstName} ${a.lastName}`.trim() || o.name)}</strong><br>${addressHtml(a)}</p>
<table><tbody>${rows}${gift ? `<tr><td class="box"></td><td class="qty">1 ×</td><td>Cadeau: ${esc(gift)}</td></tr>` : ''}</tbody></table>
${o.order.skinNote ? `<p class="note"><span class="muted">Notitie van de klant</span><br>“${esc(o.order.skinNote)}”</p>` : ''}
<div class="card"><span class="muted">Persoonlijk kaartje</span></div>
</body></html>`
}

export const printPackingSlip = (...args: Parameters<typeof packingSlipHtml>) =>
  printHtml(packingSlipHtml(...args), `pakbon-${args[0].id}.html`, 'Pakbon')
